import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Copy, Eye, Loader2, Plus, Power, PowerOff, ShieldOff } from "lucide-react";
import {
  activateUser,
  createInvite,
  deactivateUser,
  formatApiError,
  listInvites,
  listUsers,
  revokeInvite,
  toastApiError,
} from "@/lib/api";

const STATUS_STYLES = {
  pending: "border-amber-400/40 text-amber-300 bg-amber-400/10",
  used: "border-emerald-400/40 text-emerald-300 bg-emerald-400/10",
  expired: "border-neutral-500/40 text-neutral-400 bg-neutral-500/10",
  revoked: "border-red-500/40 text-red-400 bg-red-500/10",
};

export default function AdminPage() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
          Master console
        </h1>
        <div className="mt-1 text-2xl text-[var(--aurion-text)]">Admin</div>
      </div>
      <Tabs defaultValue="invites">
        <TabsList
          data-testid="admin-tabs"
          className="grid w-full max-w-md grid-cols-3 border border-[var(--aurion-border)] bg-[var(--aurion-panel)]"
        >
          <TabsTrigger data-testid="tab-invites" value="invites">
            Invites
          </TabsTrigger>
          <TabsTrigger data-testid="tab-users" value="users">
            Users
          </TabsTrigger>
          <TabsTrigger data-testid="tab-audit" value="audit" asChild>
            <Link to="/admin/audit">Audit</Link>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="invites" className="mt-6">
          <InvitesPanel />
        </TabsContent>
        <TabsContent value="users" className="mt-6">
          <UsersPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InvitesPanel() {
  const [note, setNote] = useState("");
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      setItems(await listInvites());
    } catch (e) {
      toastApiError(e);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const create = async () => {
    setCreating(true);
    try {
      const inv = await createInvite(note);
      toast.success(`Invite ${inv.code} created`);
      setNote("");
      refresh();
    } catch (e) {
      toastApiError(e);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    try {
      await revokeInvite(id);
      toast.success("Revoked");
      refresh();
    } catch (e) {
      toastApiError(e);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-6">
      <Card
        data-testid="create-invite-card"
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-6"
      >
        <div className="text-xs uppercase tracking-widest text-[var(--aurion-muted)]">
          Create invite
        </div>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="invite-note" className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Note (optional)
            </Label>
            <Input
              id="invite-note"
              data-testid="invite-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Kyle @ QuantHQ"
              className="mt-2 h-11 border-[var(--aurion-border)] bg-[var(--aurion-panel-2)]"
            />
          </div>
          <Button
            data-testid="create-invite-btn"
            onClick={create}
            disabled={creating}
            className="h-11 border border-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10 font-semibold tracking-widest text-[var(--aurion-gold)] hover:bg-[var(--aurion-gold)]/20"
          >
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            New invite (7-day)
          </Button>
        </div>
      </Card>

      <Card className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--aurion-border)] hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Code
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Status
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Note
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Used by
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Expires
              </TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="invites-tbody">
            {items === null ? (
              <TableRow>
                <TableCell colSpan={6} className="text-[var(--aurion-muted)]">
                  Loading…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-[var(--aurion-muted)]">
                  No invites yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="border-[var(--aurion-border)] hover:bg-[var(--aurion-panel-2)]"
                  data-testid={`invite-row-${inv.code}`}
                >
                  <TableCell className="font-mono text-[var(--aurion-text)]">
                    <button
                      onClick={() => copyCode(inv.code)}
                      className="group inline-flex items-center gap-2 hover:text-[var(--aurion-gold)]"
                      data-testid={`copy-invite-${inv.code}`}
                    >
                      {inv.code}
                      <Copy className="h-3 w-3 opacity-40 group-hover:opacity-100" />
                    </button>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`rounded-none text-[10px] uppercase tracking-widest ${STATUS_STYLES[inv.status]}`}
                      data-testid={`invite-status-${inv.code}`}
                    >
                      {inv.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-[var(--aurion-muted)]">
                    {inv.note || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-[var(--aurion-muted)]">
                    {inv.used_by_user
                      ? `${inv.used_by_user.name} <${inv.used_by_user.email}>`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-[var(--aurion-muted)]">
                    {inv.expires_at
                      ? new Date(inv.expires_at).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {inv.status === "pending" ? (
                      <Button
                        data-testid={`revoke-invite-${inv.code}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => revoke(inv.id)}
                        className="text-red-400 hover:bg-transparent hover:text-red-300"
                      >
                        <ShieldOff className="mr-1 h-3.5 w-3.5" />
                        Revoke
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function UsersPanel() {
  const [items, setItems] = useState(null);

  const refresh = async () => {
    try {
      setItems(await listUsers());
    } catch (e) {
      toastApiError(e);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const toggle = async (u) => {
    try {
      if (u.active) await deactivateUser(u.id);
      else await activateUser(u.id);
      toast.success(`${u.email} · ${u.active ? "deactivated" : "activated"}`);
      refresh();
    } catch (e) {
      toastApiError(e);
    }
  };

  return (
    <Card className="border-[var(--aurion-border)] bg-[var(--aurion-panel)] p-2">
      <Table>
        <TableHeader>
          <TableRow className="border-[var(--aurion-border)] hover:bg-transparent">
            <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Email
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Name
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Role
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Active
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Portfolios
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Joined
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Last login
            </TableHead>
            <TableHead className="text-right text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody data-testid="users-tbody">
          {items === null ? (
            <TableRow>
              <TableCell colSpan={8} className="text-[var(--aurion-muted)]">
                Loading…
              </TableCell>
            </TableRow>
          ) : (
            items.map((u) => (
              <TableRow
                key={u.id}
                className="border-[var(--aurion-border)] hover:bg-[var(--aurion-panel-2)]"
                data-testid={`user-row-${u.email}`}
              >
                <TableCell className="font-mono text-xs text-[var(--aurion-text)]">
                  {u.email}
                </TableCell>
                <TableCell className="text-xs text-[var(--aurion-muted)]">
                  {u.name}
                </TableCell>
                <TableCell>
                  {u.role === "master" ? (
                    <Badge
                      variant="outline"
                      className="rounded-none border-[var(--aurion-gold)]/40 bg-[var(--aurion-gold)]/10 text-[10px] uppercase tracking-widest text-[var(--aurion-gold)]"
                    >
                      master
                    </Badge>
                  ) : (
                    <span className="text-xs text-[var(--aurion-muted)]">member</span>
                  )}
                </TableCell>
                <TableCell>
                  <span
                    data-testid={`user-active-${u.email}`}
                    className={`text-[10px] uppercase tracking-widest ${
                      u.active ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {u.active ? "active" : "deactivated"}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-[var(--aurion-muted)]">
                  {u.portfolio_count}
                </TableCell>
                <TableCell className="text-xs text-[var(--aurion-muted)]">
                  {new Date(u.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-xs text-[var(--aurion-muted)]">
                  {u.last_login ? new Date(u.last_login).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {u.role !== "master" ? (
                      <Button
                        data-testid={`toggle-user-${u.email}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => toggle(u)}
                        className={
                          u.active
                            ? "text-red-400 hover:bg-transparent hover:text-red-300"
                            : "text-emerald-400 hover:bg-transparent hover:text-emerald-300"
                        }
                      >
                        {u.active ? (
                          <PowerOff className="mr-1 h-3.5 w-3.5" />
                        ) : (
                          <Power className="mr-1 h-3.5 w-3.5" />
                        )}
                        {u.active ? "deactivate" : "activate"}
                      </Button>
                    ) : null}
                    <Link
                      to={`/admin/users/${u.id}`}
                      data-testid={`cloak-user-${u.email}`}
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[var(--aurion-gold)] hover:bg-transparent hover:text-[var(--aurion-gold)]"
                      >
                        <Eye className="mr-1 h-3.5 w-3.5" />
                        Cloak
                      </Button>
                    </Link>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
